/**
 * <back-in-stock-alert>
 *
 * Wrapper for the "Sold out - Notify me" button rendered by
 * snippets/back-in-stock-alert.liquid. Liquid decides whether the button
 * exists; this element only keeps that decision in sync when the shopper
 * changes variant, so the button is never present for an available variant.
 *
 * How it stays in sync:
 * 1. optionValueSelectionChange (theme pubsub) - a new option was picked and
 *    the theme starts fetching the section. The current state is no longer
 *    valid, so the button is removed. This also covers unavailable option
 *    combinations, where the theme never publishes variantChange.
 * 2. variantChange (theme pubsub) - the theme has fetched the re-rendered
 *    section (Section Rendering API). The matching <back-in-stock-alert> in
 *    that response is the source of truth, so its children and `hidden`
 *    state are copied across. No markup is built in JS.
 *
 * Opening the drawer:
 * <back-in-stock-drawer> binds its trigger buttons once, in its own
 * connectedCallback. On variant change the theme re-renders the drawer
 * before this element re-renders its button, so a freshly inserted button
 * would never be bound. A single delegated click listener here opens the
 * drawer instead. On first page load the drawer has already bound the button
 * itself; ModalElement#show() returns early when already open, so the second
 * call is a harmless no-op.
 */
if (!customElements.get('back-in-stock-alert')) {
  class BackInStockAlert extends HTMLElement {
    /** @type {Array<() => void>} pubsub unsubscribe callbacks */
    #unsubscribers = [];

    connectedCallback() {
      const pubsub = window.theme?.pubsub;

      this.addEventListener('click', this.#onClick);

      // theme.js is loaded (deferred) in the <head>, so pubsub should always
      // exist. Without it, the server-rendered state is still correct; only
      // live variant updates are lost.
      if (!pubsub) return;

      const { optionValueSelectionChange, variantChange } = pubsub.PUB_SUB_EVENTS;

      this.#unsubscribers = [
        pubsub.subscribe(optionValueSelectionChange, this.#onSelectionChange),
        pubsub.subscribe(variantChange, this.#onVariantChange)
      ];
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
      this.#unsubscribers = [];
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get productId() {
      return this.dataset.productId;
    }

    /**
     * The <product-info> that owns this product's variant picker.
     * @returns {HTMLElement|null}
     */
    get productInfo() {
      return document.getElementById(`ProductInfo-${this.sectionId}-${this.productId}`);
    }

    /**
     * A new option value was selected. Remove the button until the theme
     * returns the re-rendered section for the new selection.
     */
    #onSelectionChange = ({ data: { event } }) => {
      const target = event?.target;
      if (!target || !this.productInfo?.contains(target)) return;

      // Add-on pickers live inside product-info but don't change this
      // product's variant (same check as ProductInfo#handleOptionValueChange).
      if (target.closest('variant-picker')?.getAttribute('data-context') === 'addon') return;

      this.#clear();
    };

    /**
     * The theme fetched the re-rendered section for the new variant. Mirror
     * the server-rendered element for this product.
     */
    #onVariantChange = ({ data: { sectionId, productId, parsedHTML } }) => {
      // String() because the theme may pass ids as numbers or strings.
      if (String(sectionId) !== this.sectionId || String(productId) !== this.productId) return;

      const source = parsedHTML?.getElementById(this.id);

      if (!source) {
        this.#clear();
        return;
      }

      // importNode (rather than innerHTML) adopts the nodes into this
      // document so customised built-ins such as is="hover-button" upgrade.
      const fresh = document.importNode(source, true);

      this.replaceChildren(...fresh.childNodes);
      this.hidden = fresh.hidden;
    };

    /**
     * Opens the drawer referenced by the button's aria-controls.
     * See the "Opening the drawer" note at the top of this file.
     */
    #onClick = (event) => {
      const trigger = event.target.closest('[aria-controls]');
      if (!trigger || !this.contains(trigger)) return;

      const drawer = document.getElementById(trigger.getAttribute('aria-controls'));
      if (typeof drawer?.show !== 'function') return;

      event.preventDefault();
      drawer.show(trigger);
    };

    /** Removes the button from the DOM and hides the empty wrapper. */
    #clear() {
      this.replaceChildren();
      this.hidden = true;
    }
  }

  customElements.define('back-in-stock-alert', BackInStockAlert);
}
